import {useMediaQueries, useQueryParams} from 'presentation/pages';
import {translations} from 'presentation/translations-new';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {getUnitsByDistanceTitle} from 'presentation/modules/booking/shared/utils/getUnitsByDistanceTitle';
import {HandleSelectCardParams, UseExamUnitsSelfNetworkProps} from './types';
import {MIN_DISTANCE_IN_KM_EXAM} from 'presentation/modules/booking/exam/ui/components/ExamSearchBar/hooks/useExamSearchBar/constants';
import {MAX_DISTANCE_FILTER_UNITS} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';

const {changeDistance, searchNearbyUnitsHealthExam, selfNetwork} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function useExamUnitsSelfNetwork({handleNextStep, unitsList}: UseExamUnitsSelfNetworkProps) {
	const {
		SEARCH_PARAMS: {query, localityDistance},
		clearSearch,
	} = useQueryParams();
	const {isMobile} = useMediaQueries();
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);

	const {specialtyName, isManualLocation} = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);

	const isDistanceFilterActive = Boolean(localityDistance);
	const filterIsActive = isDistanceFilterActive;

	const showUnitsByDistanceTitle = () => {
		if (isManualLocation && !listByDistanceIsEmpty) return true;

		return isDistanceFilterActive && localityDistance !== MIN_DISTANCE_IN_KM_EXAM;
	};

	const unitsListState = useUnitsListStore(state => state.unitsList);

	const hasAnyUnit = Boolean(
		unitsListState?.units?.unitsByLocality?.length ||
		unitsListState?.units?.unitsByDistance?.length ||
		unitsListState?.units?.unitsSupportAccredited?.length,
	);

	const listByDistanceLength = unitsList.units.unitsByDistance?.length;
	const listByDistanceIsEmpty = listByDistanceLength === 0;

	const listByLocalityLength = unitsList.units.unitsByLocality?.length;
	const listByLocalityIsEmpty = listByLocalityLength === 0;

	const unitsByDistanceTitle = getUnitsByDistanceTitle(listByDistanceLength, localityDistance);

	const hideComponent = query && hasAnyUnit && listByLocalityIsEmpty && listByDistanceIsEmpty;

	const showEmptyAlert = listByLocalityIsEmpty && listByDistanceIsEmpty;

	const isDistanceAtMax = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS;

	const shouldHideDrawerButton = isUnitsNetworksEnabled && showEmptyAlert && isDistanceAtMax;

	const handleSelectCard = (params: HandleSelectCardParams) => {
		setBookingData(prev => ({
			...prev,
			unitCode: params.code,
			unitName: params.name,
			unitAddress: params.address,
			bookingRedirect: false,
		}));

		clearSearch();
		handleNextStep();
	};

	const openDrawerButtonLabel = () => {
		if (!filterIsActive) return searchNearbyUnitsHealthExam;

		return changeDistance;
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const getEmptyAlertDescription = () => {
		if (isDistanceFilterActive && !(isUnitsNetworksEnabled && isDistanceAtMax)) {
			return `${selfNetwork.emptyDescriptionInKmPart1}${localityDistance}${selfNetwork.emptyDescriptionInKmPart2}`;
		}

		return selfNetwork.emptyDescription;
	};

	return {
		listByDistanceIsEmpty,
		listByLocalityIsEmpty,
		showUnitsByDistanceTitle,
		unitsByDistanceTitle,
		handleSelectCard,
		filterIsActive,
		openDrawerButtonLabel,
		handleOpenDrawer,
		isMobile,
		query,
		specialtyName,
		hideComponent,
		getEmptyAlertDescription,
		showEmptyAlert,
		shouldHideDrawerButton,
	};
}
